import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, Ban } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/adminApi";
import { formatApiErrorDetail } from "../../lib/api";
import { etDateTimeToUtcIso, isoToEtTime, prettyTime, todayInET } from "../../lib/time";
import ConfirmDialog from "../../components/admin/ConfirmDialog";

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const WEEKDAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function addMonth(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const idx = y * 12 + (m - 1) + delta;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, "0")}`;
}
function buildGrid(ym) {
  const [y, m] = ym.split("-").map(Number);
  const firstDay = new Date(y, m - 1, 1).getDay();
  const days = new Date(y, m, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= days; d++)
    cells.push(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function AdminCalendar() {
  const [yachts, setYachts] = useState([]);
  const [yachtId, setYachtId] = useState("");
  const [month, setMonth] = useState(() => todayInET().slice(0, 7));
  const [events, setEvents] = useState({ bookings: [], blocks: [] });
  const [selectedDay, setSelectedDay] = useState(null);
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [confirmBlockDelete, setConfirmBlockDelete] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/yachts").then((r) => {
      setYachts(r.data);
      const first = r.data.find((y) => y.status === "active") || r.data[0];
      if (first && !yachtId) setYachtId(first.id);
    });
  }, []);  // eslint-disable-line

  useEffect(() => {
    if (!yachtId) return;
    const [y, m] = month.split("-").map(Number);
    const from = new Date(Date.UTC(y, m - 1, 1)).toISOString();
    const toDt = new Date(Date.UTC(y, m, 1)).toISOString();
    Promise.all([
      adminApi.get("/admin/bookings", { params: { yacht_id: yachtId, from_date: from.slice(0, 10), to_date: toDt.slice(0, 10), page_size: 200 } }),
      adminApi.get("/admin/availability-blocks", { params: { yacht_id: yachtId, from: from, to: toDt } }),
    ])
      .then(([bR, blR]) => setEvents({ bookings: bR.data.items || [], blocks: blR.data || [] }))
      .catch(() => setEvents({ bookings: [], blocks: [] }));
  }, [yachtId, month]);

  const cells = useMemo(() => buildGrid(month), [month]);
  const [y, m] = month.split("-").map(Number);

  const eventsByDay = useMemo(() => {
    const out = {};
    (events.bookings || []).forEach((b) => {
      if (b.booking_status === "cancelled") return;
      const d = new Date(b.start_datetime);
      const iso = new Intl.DateTimeFormat("en-CA", {
        timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit",
      }).format(d);
      (out[iso] ||= { bookings: [], blocks: [] }).bookings.push(b);
    });
    (events.blocks || []).forEach((bl) => {
      const d = new Date(bl.start_datetime);
      const iso = new Intl.DateTimeFormat("en-CA", {
        timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit",
      }).format(d);
      (out[iso] ||= { bookings: [], blocks: [] }).blocks.push(bl);
    });
    return out;
  }, [events]);

  const dayEvents = selectedDay ? eventsByDay[selectedDay] || { bookings: [], blocks: [] } : null;

  return (
    <div data-testid="admin-calendar">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="hbe-eyebrow">Operations</div>
          <h1 className="mt-2 font-serif text-3xl md:text-4xl">Calendar</h1>
        </div>
        <select
          className="hbe-input max-w-xs"
          value={yachtId}
          onChange={(e) => setYachtId(e.target.value)}
          data-testid="cal-yacht-select"
        >
          {yachts.map((y) => (
            <option key={y.id} value={y.id}>{y.name || y.slug}</option>
          ))}
        </select>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px]">
        <div className="hbe-card p-5">
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => setMonth(addMonth(month, -1))}
              className="p-2 rounded-full hover:bg-hbe-soft text-hbe-primary"
              aria-label="Previous month"
              data-testid="cal-prev"
            ><ChevronLeft size={20} /></button>
            <div className="font-serif text-lg text-hbe-primary">{MONTH_NAMES[m - 1]} {y}</div>
            <button
              type="button"
              onClick={() => setMonth(addMonth(month, 1))}
              className="p-2 rounded-full hover:bg-hbe-soft text-hbe-primary"
              aria-label="Next month"
              data-testid="cal-next"
            ><ChevronRight size={20} /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-xs text-hbe-ink/55 mb-1">
            {WEEKDAYS.map((w) => (
              <div key={w} className="text-center py-1 uppercase tracking-wider">{w}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((dateStr, i) => {
              if (!dateStr) return <div key={i} className="h-16" />;
              const ev = eventsByDay[dateStr];
              const hasBook = ev?.bookings?.length > 0;
              const hasBlock = ev?.blocks?.length > 0;
              const selected = selectedDay === dateStr;
              const day = Number(dateStr.slice(-2));
              return (
                <button
                  key={dateStr}
                  type="button"
                  onClick={() => setSelectedDay(dateStr)}
                  className={`h-16 rounded-md flex flex-col items-start p-1.5 border transition text-left ${
                    selected ? "bg-hbe-primary text-white border-hbe-primary" : "border-hbe-line hover:bg-hbe-soft"
                  }`}
                  data-testid={`cal-day-${dateStr}`}
                >
                  <span className="text-xs font-medium">{day}</span>
                  <span className="mt-auto flex flex-wrap gap-0.5">
                    {hasBlock && <span className="text-[9px] px-1 py-0.5 rounded bg-red-100 text-red-700">Block</span>}
                    {hasBook && <span className="text-[9px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-800">{ev.bookings.length}b</span>}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Side panel */}
        <aside className="hbe-card p-5">
          {!selectedDay ? (
            <div className="text-sm text-hbe-ink/60">Select a day to see events and take action.</div>
          ) : (
            <>
              <div className="hbe-eyebrow">Selected day</div>
              <div className="mt-1 font-serif text-xl text-hbe-primary">{selectedDay}</div>

              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => { setShowBlockForm(true); setShowBookingForm(false); }}
                  className="hbe-btn-ghost text-xs px-4 py-2 min-h-0"
                  data-testid="cal-add-block"
                ><Ban size={14} className="mr-1" /> Add block</button>
                <button
                  type="button"
                  onClick={() => { setShowBookingForm(true); setShowBlockForm(false); }}
                  className="hbe-btn-primary text-xs px-4 py-2 min-h-0"
                  data-testid="cal-add-booking"
                ><Plus size={14} className="mr-1" /> Manual booking</button>
              </div>

              <div className="mt-6 space-y-4">
                {dayEvents.blocks.map((bl) => (
                  <div key={bl.id} className="border border-hbe-line rounded-md p-3 text-sm" data-testid={`block-${bl.id}`}>
                    <div className="font-medium text-red-700">Block</div>
                    <div className="text-hbe-ink/70">
                      {prettyTime(isoToEtTime(bl.start_datetime))} — {prettyTime(isoToEtTime(bl.end_datetime))}
                    </div>
                    {bl.reason && <div className="mt-1 text-xs text-hbe-ink/60">{bl.reason}</div>}
                    <button
                      type="button"
                      onClick={() => setConfirmBlockDelete(bl.id)}
                      className="mt-2 text-xs text-red-700 hover:underline"
                      data-testid={`block-delete-${bl.id}`}
                    >Delete block</button>
                  </div>
                ))}
                {dayEvents.bookings.map((b) => (
                  <Link
                    to={`/admin/bookings/${b.id}`}
                    key={b.id}
                    className="block border border-hbe-line rounded-md p-3 text-sm hover:bg-hbe-soft/40"
                    data-testid={`booking-${b.confirmation_number}`}
                  >
                    <div className="font-mono text-xs text-hbe-primary">{b.confirmation_number}</div>
                    <div className="text-hbe-ink/70 mt-0.5">
                      {prettyTime(isoToEtTime(b.start_datetime))} — {prettyTime(isoToEtTime(b.end_datetime))} · {b.customer_name}
                    </div>
                  </Link>
                ))}
              </div>

              {showBlockForm && (
                <BlockForm
                  yachtId={yachtId}
                  date={selectedDay}
                  onDone={() => { setShowBlockForm(false); setMonth(month); /* trigger refresh */ setYachtId(yachtId); }}
                  onCancel={() => setShowBlockForm(false)}
                />
              )}
              {showBookingForm && (
                <ManualBookingForm
                  yachtId={yachtId}
                  yachtOptions={yachts}
                  date={selectedDay}
                  onDone={() => { setShowBookingForm(false); setYachtId(yachtId); }}
                  onCancel={() => setShowBookingForm(false)}
                />
              )}
            </>
          )}
        </aside>
      </div>

      <ConfirmDialog
        open={Boolean(confirmBlockDelete)}
        onClose={() => setConfirmBlockDelete(null)}
        onConfirm={async () => {
          const bid = confirmBlockDelete;
          setConfirmBlockDelete(null);
          try {
            await adminApi.delete(`/admin/availability-blocks/${bid}`);
            toast.success("Block deleted.");
            setYachtId(yachtId); // trigger refresh
          } catch (e) { toast.error(formatApiErrorDetail(e)); }
        }}
        title="Delete this block?"
        confirmLabel="Delete"
        danger
      />
    </div>
  );
}

function BlockForm({ yachtId, date, onDone, onCancel }) {
  const [start, setStart] = useState("08:00");
  const [end, setEnd] = useState("22:00");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit() {
    setSaving(true);
    try {
      await adminApi.post("/admin/availability-blocks", {
        yacht_id: yachtId,
        start_datetime: etDateTimeToUtcIso(date, start),
        end_datetime: etDateTimeToUtcIso(date, end),
        block_type: "time_range",
        reason: reason || null,
      });
      toast.success("Block created.");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setSaving(false); }
  }

  return (
    <div className="mt-6 border-t border-hbe-line pt-5" data-testid="cal-block-form">
      <div className="font-medium mb-3">Add availability block</div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="hbe-label text-xs">Start (ET)</label>
          <input type="time" className="hbe-input" value={start} onChange={(e) => setStart(e.target.value)} data-testid="bf-start" />
        </div>
        <div>
          <label className="hbe-label text-xs">End (ET)</label>
          <input type="time" className="hbe-input" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="bf-end" />
        </div>
      </div>
      <label className="hbe-label text-xs mt-3">Reason</label>
      <input className="hbe-input" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="bf-reason" />
      <div className="mt-4 flex gap-2">
        <button type="button" className="hbe-btn-primary text-xs px-4 py-2 min-h-0" onClick={submit} disabled={saving} data-testid="bf-save">
          {saving ? "Saving…" : "Save block"}
        </button>
        <button type="button" className="hbe-btn-ghost text-xs px-4 py-2 min-h-0" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function ManualBookingForm({ yachtId, yachtOptions, date, onDone, onCancel }) {
  const yacht = yachtOptions.find((y) => y.id === yachtId);
  const [time, setTime] = useState("12:00");
  const [duration, setDuration] = useState(4);
  const [guests, setGuests] = useState(2);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!name.trim() || !email.trim() || !phone.trim()) {
      toast.error("Please fill name, email, and phone.");
      return;
    }
    setSaving(true);
    try {
      await adminApi.post("/admin/bookings", {
        yacht_id: yachtId,
        start_datetime: etDateTimeToUtcIso(date, time),
        duration_hours: Number(duration),
        guest_count: Number(guests),
        customer_name: name,
        customer_email: email,
        customer_phone: phone,
      });
      toast.success("Manual booking created.");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setSaving(false); }
  }

  return (
    <div className="mt-6 border-t border-hbe-line pt-5" data-testid="cal-manual-form">
      <div className="font-medium mb-3">Create manual booking</div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="hbe-label text-xs">Time (ET)</label>
          <input type="time" className="hbe-input" value={time} onChange={(e) => setTime(e.target.value)} data-testid="mb-time" />
        </div>
        <div>
          <label className="hbe-label text-xs">Duration</label>
          <select className="hbe-input" value={duration} onChange={(e) => setDuration(Number(e.target.value))} data-testid="mb-duration">
            {[4,5,6,7,8].map((h) => <option key={h} value={h}>{h}h</option>)}
          </select>
        </div>
        <div>
          <label className="hbe-label text-xs">Guests</label>
          <input
            type="number"
            min={1}
            max={yacht?.capacity || 12}
            className="hbe-input"
            value={guests}
            onChange={(e) => setGuests(Number(e.target.value))}
            data-testid="mb-guests"
          />
        </div>
      </div>
      <label className="hbe-label text-xs mt-3">Name</label>
      <input className="hbe-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="mb-name" />
      <label className="hbe-label text-xs mt-3">Email</label>
      <input type="email" className="hbe-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="mb-email" />
      <label className="hbe-label text-xs mt-3">Phone</label>
      <input className="hbe-input" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="mb-phone" />
      <div className="mt-4 flex gap-2">
        <button type="button" className="hbe-btn-primary text-xs px-4 py-2 min-h-0" onClick={submit} disabled={saving} data-testid="mb-save">
          {saving ? "Saving…" : "Create booking"}
        </button>
        <button type="button" className="hbe-btn-ghost text-xs px-4 py-2 min-h-0" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
